import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Project, ProjectDashboard } from '@faktorplus/shared';
import { apiRequest } from '../lib/api-client';
import { queryKeys } from '../lib/query-keys';

/**
 * The date range the site screen is looking at, as it appears in the URL.
 *
 * A type alias rather than an interface on purpose: only aliases get an
 * implicit index signature, which is what lets this be passed straight to the
 * query builder and the query key.
 */
export type ProjectPeriod = {
  from?: string;
  to?: string;
};

export function useProject(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.projects.detail(id ?? ''),
    queryFn: () => apiRequest<Project>(`/projects/${id}`),
    enabled: Boolean(id),
  });
}

export function useProjectDashboard(id: string | undefined, period: ProjectPeriod) {
  return useQuery({
    queryKey: queryKeys.projects.dashboard(id ?? '', period),
    queryFn: () => apiRequest<ProjectDashboard>(`/projects/${id}/dashboard`, { query: period }),
    enabled: Boolean(id),
    placeholderData: (previous) => previous,
  });
}

/** The "Archiwum budów" view — archived sites only, newest first. */
export function useArchivedProjects(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...queryKeys.projects.all, 'archived'],
    queryFn: () => apiRequest<Project[]>('/projects', { query: { archived: true } }),
    enabled: options.enabled ?? true,
  });
}

/**
 * Archiving moves the site — and its invoices — out of every list, so the
 * invalidation sweeps wide: project lists, pickers, the register, reports.
 */
function useInvalidateProjects() {
  const queryClient = useQueryClient();

  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.reports.dashboard });
  };
}

export function useArchiveProject() {
  const invalidate = useInvalidateProjects();

  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<Project>(`/projects/${id}/archive`, { method: 'POST' }),
    onSuccess: invalidate,
  });
}

export function useUnarchiveProject() {
  const invalidate = useInvalidateProjects();

  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<Project>(`/projects/${id}/unarchive`, { method: 'POST' }),
    onSuccess: invalidate,
  });
}
